import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// Polyfill for process (required by Google Maps API)
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  IconButton,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polyline,
  DirectionsRenderer,
  DirectionsService
} from '@react-google-maps/api';
import { useParams } from 'react-router-dom';
import BookingAPI from '../common/bookingApi';
import { GoogleMapApiConfig } from '../config/GoogleMapApiConfig';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import trackCarIcon from '../assets/img/track_Car.png';
import signal from '../assets/img/signal.png';
import locationPin from '../assets/img/location-pin.png';
import { useDriverLocationSimple } from '../hooks/useDriverLocationSimple';
import { useBookingDataSelector } from '../hooks/useBookingDataSelector';
import { api } from 'common';

const libraries = ["geometry", "drawing", "places"];

if (typeof process === 'undefined') {
  window.process = { env: {} };
}

const ShareTrip = () => {
  // Translation
  const { t } = useTranslation();

  // URL Parameters
  const { bookingId } = useParams();

  // Redux
  const dispatch = useDispatch();
  const { fetchSettings } = api;

  // State Management
  const [routeCoords, setRouteCoords] = useState(null);
  const [arrivalTime, setArrivalTime] = useState(0);
  const [directions, setDirections] = useState(null);

  // Popup Info State
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);

  // Refs
  const mapRef = useRef();
  const isMapInitialized = useRef(false);
  const userHasInteracted = useRef(false);
  const [mapCenter, setMapCenter] = useState(null);

  // Settings state
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // ✅ SOLUSI: Gunakan custom hook untuk driver location dengan selector
  const {
    driverLocation,
    isTracking,
    error: trackingError,
    shouldTrack
  } = useDriverLocationSimple(bookingId);

  // ✅ SOLUSI: Gunakan custom hook untuk booking data dengan selector
  const {
    bookingData,
    driverInfo,
    loading,
    error,
    isRealTime,
    ensureBookingInReduxStore
  } = useBookingDataSelector(bookingId);

  // Get settings from Redux store
  const settings = useSelector((state) => state.settingsdata?.settings);

  // Check if Google Maps is loaded from App.js
  const [isLoaded, setIsLoaded] = useState(false);

  // ✅ SOLUSI: Fetch settings if not available in Redux store
  useEffect(() => {
    if (!settings && !settingsLoaded) {
      console.log('🔧 Settings not available in Redux store, fetching...');
      dispatch(fetchSettings());
      setSettingsLoaded(true);
    }
  }, [settings, settingsLoaded, dispatch, fetchSettings]);

  // Debug settings loading
  useEffect(() => {
    console.log('🔧 ShareTrip Settings Debug:', {
      settings: !!settings,
      share_trip: settings?.share_trip,
      bookingData: !!bookingData,
      bookingStatus: bookingData?.status,
      isShareTripEnabled: bookingData && 
        ['ACCEPTED', 'STARTED'].includes(bookingData.status) &&
        settings?.share_trip
    });
  }, [settings, bookingData]);

  useEffect(() => {
    // Check if Google Maps is available
    if (window.google && window.google.maps) {
      setIsLoaded(true);
    } else {
      // Wait for Google Maps to load
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          setIsLoaded(true);
          clearInterval(checkGoogleMaps);
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => {
        clearInterval(checkGoogleMaps);
      }, 10000);
    }
  }, []);

  // Map Options
  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    clickableIcons: false,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    mapTypeControl: true,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: true,
    gestureHandling: 'greedy',
    draggable: true,
    keyboardShortcuts: true,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  }), []);

  // Handle marker click
  const handleMarkerClick = useCallback((markerType, position, data) => {
    setSelectedMarker({ type: markerType, data, position });
    setPopupPosition(position);
  }, []);

  // Handle map click to close popup
  const handleMapClick = useCallback(() => {
    setSelectedMarker(null);
    setPopupPosition(null);
  }, []);

  // ✅ SOLUSI: Booking data sudah dihandle oleh useBookingDataSelector hook
  // Tidak perlu manual fetch lagi karena sudah menggunakan selector

  // ✅ SOLUSI: Driver location sudah dihandle oleh useDriverLocationSimple hook
  // Tidak perlu manual tracking lagi karena sudah menggunakan selector

  // Set initial map center only once - never change after user interaction
  useEffect(() => {
    console.log('fire me', mapCenter, userHasInteracted.current);
    if (!mapCenter || !userHasInteracted.current) {
      if (driverLocation) {
        console.log('Setting initial map center to driver location:', driverLocation);
        setMapCenter({
          lat: driverLocation.lat,
          lng: driverLocation.lng
        });
      } else if (bookingData) {
        console.log('Setting initial map center to pickup location:', bookingData.pickup);
        setMapCenter({
          lat: bookingData.pickup.lat,
          lng: bookingData.pickup.lng
        });
      } else {
        setMapCenter({ lat: -6.2088, lng: 106.8456 });
      }
    }
  }, [driverLocation, bookingData, mapCenter]);

  const [mapZoom, setMapZoom] = useState(15);

  // Calculate route using Google Directions API
  const calculateRoute = useCallback(() => {
    if (!bookingData || !isLoaded) return;

    const directionsService = new window.google.maps.DirectionsService();

    const startPoint = driverLocation || {
      lat: bookingData.pickup.lat,
      lng: bookingData.pickup.lng
    };

    const endPoint = bookingData.status === 'ACCEPTED' ?
      { lat: bookingData.pickup.lat, lng: bookingData.pickup.lng } :
      { lat: bookingData.drop.lat, lng: bookingData.drop.lng };

    directionsService.route(
      {
        origin: startPoint,
        destination: endPoint,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
          setArrivalTime(Math.round(result.routes[0].legs[0].duration.value / 60));
        } else {
          console.error('Directions request failed due to ' + status);
        }
      }
    );
  }, [bookingData, driverLocation, isLoaded]);

  // ✅ SOLUSI: Booking data dan driver location sudah dihandle oleh selector hooks
  // Tidak perlu manual effects lagi

  // ✅ SOLUSI: Log tracking status untuk debugging
  useEffect(() => {
    if (isTracking) {
      console.log('📍 Driver location tracking is ACTIVE');
    } else {
      console.log('📍 Driver location tracking is INACTIVE');
    }
  }, [isTracking]);

  // ✅ SOLUSI: Log booking data status untuk debugging
  useEffect(() => {
    if (bookingData) {
      console.log('📍 Booking data updated:', {
        id: bookingData.id,
        status: bookingData.status,
        isRealTime,
        driver: bookingData.driver_name
      });
    }
  }, [bookingData, isRealTime]);

  useEffect(() => {
    if (bookingData && driverLocation) {
      calculateRoute();
    }
  }, [bookingData, driverLocation, calculateRoute]);


  // Loading state
  if (loading || (!settings && settingsLoaded)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          {!settings && settingsLoaded ? t('loading_settings') || 'Loading settings...' : t('loading_trip_data')}...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error || trackingError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" color="error" gutterBottom>
            {t('error')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {error || trackingError}
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => window.location.reload()}
          >
            {t('retry')}
          </Button>
        </Box>
      </Container>
    );
  }

  // No booking data
  if (!bookingData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" gutterBottom>
            {t('trip_not_found')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('trip_not_found_message')}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!['ACCEPTED', 'STARTED', 'ARRIVED'].includes(bookingData.status)) {
    return (
      <Box sx={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        padding: 2,
        gap: 2,
        backgroundColor: '#f5f5f5',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      >
        <div className="material-icons" style={{ color: '#1976d2', fontSize: 24, marginRight: 8 }}>
          <Typography variant="h4" color="error" gutterBottom>
            {t('trip_not_found')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('trip_not_found_message')}
          </Typography>
        </div>
      </Box>
    );
  }

  // Check if share_trip is enabled - use local settings check
  const isShareTripEnabled = bookingData && 
    ['ACCEPTED', 'STARTED', 'ARRIVED'].includes(bookingData.status) &&
    settings?.share_trip;

  if (!isShareTripEnabled) {
    return (
      <Box sx={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        padding: 2,
        gap: 2,
        backgroundColor: '#f5f5f5',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="h4" color="error" gutterBottom>
          {t('share_trip_disabled') || 'Share trip disabled'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: { md: 'calc(100vh - 100px)', xs: '1000px' },
      display: 'flex',
      padding: '20px',
      paddingBottom: '50px',
      gap: 2,
      backgroundColor: '#f5f5f5',
      flexDirection: { xs: 'column', md: 'row' }
    }}>
      {/* Map Section - Left Side (70%) */}
      <Box sx={{
        flex: '0 0 70%',
        height: { md: 'calc(100vh - 100px)', xs: '1000px' },
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        backgroundColor: 'white',
        paddingBottom: '50px'
      }}>

        {isLoaded ? (
          <Box sx={{ height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: "10px" }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span className="material-icons" style={{ color: '#1976d2', fontSize: 24, marginRight: 8 }}>
                  map
                </span>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                  {t("map_trip")}
                </Typography>
                {/* ✅ SOLUSI: Status tracking indicator */}
                {isTracking && (
                  <Chip
                    label={t("live")}
                    size="small"
                    sx={{
                      ml: 1,
                      backgroundColor: '#4caf50',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem'
                    }}
                  />
                )}
                {!isTracking && shouldTrack && (
                  <Chip
                    label={t("connecting")}
                    size="small"
                    sx={{
                      ml: 1,
                      backgroundColor: '#ff9800',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem'
                    }}
                  />
                )}
                {/* ✅ SOLUSI: Indikator data source */}
                {isRealTime && (
                  <Chip
                    label={t("real_time")}
                    size="small"
                    sx={{
                      ml: 1,
                      backgroundColor: '#2196f3',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem'
                    }}
                  />
                )}
                {/* ✅ DEBUG: Button untuk memastikan booking ada di Redux store */}
              </Box>
              {arrivalTime > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t("eta")}: {arrivalTime} {t("minutes")}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: { xs: 'block', md: 'none', padding: "10px" } }}>
              {/* Pickup Section */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#4caf50',
                    mr: 2,
                    mt: 0.5,
                    flexShrink: 0
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {t("pickup")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                    {bookingData.pickupAddress}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    {bookingData.created_at ?
                      new Date(parseInt(bookingData.created_at)).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      }) :
                      '14:30'
                    }
                  </Typography>
                </Box>
              </Box>

              {/* Destination Section */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#f44336',
                    mr: 2,
                    mt: 0.5,
                    flexShrink: 0
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {t("destination")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                    {bookingData.dropAddress}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    {t("eta")}: {arrivalTime > 0 ? `${arrivalTime} ${t("minutes")}` : t("calculating")}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <GoogleMap
              key={`map-${bookingId}-${isMapInitialized.current ? 'initialized' : 'loading'}`}
              mapContainerStyle={{
                width: '100%',
                height: '100%',
                minHeight: '400px'
              }}
              center={mapCenter}
              zoom={mapZoom}
              options={mapOptions}
              onZoomChanged={() => {
                console.log('zoom_changed');
                if (mapRef.current) {
                  setMapZoom(mapRef.current.getZoom());
                }
              }}
              onLoad={(map) => {
                console.log('GoogleMap loaded successfully');
                mapRef.current = map;
                
                // // Track user interactions
                // map.addListener('dragstart', () => {
                  userHasInteracted.current = true;
                //   console.log('User started dragging - map center will be preserved');
                // });
                
                // map.addListener('zoom_changed', () => {
                //   userHasInteracted.current = true;
                //   console.log('User changed zoom - map center will be preserved');
                // });
                
                
                // Set initial bounds to fit all markers only if user hasn't interacted
                if (bookingData && !userHasInteracted.current) {
                  // const bounds = new window.google.maps.LatLngBounds();
                  
                  // Add pickup location
                  // bounds.extend(new window.google.maps.LatLng(
                  //   bookingData.pickup.lat,
                  //   bookingData.pickup.lng
                  // ));
                  
                  // Add destination location
                  // bounds.extend(new window.google.maps.LatLng(
                  //   bookingData.drop.lat,
                  //   bookingData.drop.lng
                  // ));
                  
                  // Add driver location if available
                  // if (driverLocation) {
                  //   bounds.extend(new window.google.maps.LatLng(
                  //     driverLocation.lat,
                  //     driverLocation.lng
                  //   ));
                  // }
                  
                  // Fit bounds to show all locations
                  // map.fitBounds(bounds);
                  
                  // Set a minimum zoom level to prevent too much zoom out
                  // const listener = window.google.maps.event.addListener(map, 'bounds_changed', () => {
                  //   console.log('bounds_changed');
                  //   //set zoom
                  //   setMapZoom(map.getZoom());
                  // });
                }
              }}
              onClick={handleMapClick}
            >

              {/* Driver Marker - Using track_Car.png */}
              {driverLocation && (
                <Marker
                  position={{
                    lat: driverLocation.lat,
                    lng: driverLocation.lng
                  }}
                  icon={{
                    url: trackCarIcon,
                    scaledSize: new window.google.maps.Size(40, 40),
                    anchor: new window.google.maps.Point(20, 40)
                  }}
                  zIndex={1000}
                  onClick={() => handleMarkerClick('driver', {
                    lat: driverLocation.lat,
                    lng: driverLocation.lng
                  }, {
                    name: bookingData.driver_name || 'Driver',
                    phone: driverInfo?.driver_contact || '',
                    rating: bookingData.driverRating || 0,
                    status: bookingData?.status || t('unknown'),
                    lastUpdate: driverLocation.timestamp ?
                      new Date(driverLocation.timestamp).toLocaleTimeString() :
                      t('just_now')
                  })}
                />
              )}

              {/* Pickup Marker */}
              <Marker
                position={{
                  lat: bookingData.pickup.lat,
                  lng: bookingData.pickup.lng
                }}
                icon={{
                  url: locationPin,
                  scaledSize: new window.google.maps.Size(50, 50),
                  anchor: new window.google.maps.Point(15, 30)
                }}
                onClick={() => handleMarkerClick('pickup', {
                  lat: bookingData.pickup.lat,
                  lng: bookingData.pickup.lng
                },
                {
                  type: 'Pickup Location',
                  address: bookingData.pickupAddress,
                  status: 'Waiting for driver',
                  time: bookingData.created_at ? new Date(parseInt(bookingData.created_at)).toLocaleString() : 'Unknown'
                })}
              />

              {/* Destination Marker */}
              <Marker
                position={{
                  lat: bookingData.drop.lat,
                  lng: bookingData.drop.lng
                }}
                icon={{
                  url: signal,
                  scaledSize: new window.google.maps.Size(50, 50),
                  anchor: new window.google.maps.Point(15, 30)
                }}
                onClick={() => handleMarkerClick('destination', {
                  lat: bookingData.drop.lat,
                  lng: bookingData.drop.lng
                }, {
                  type: 'Destination',
                  address: bookingData.dropAddress,
                  status: 'Final destination',
                  estimatedTime: arrivalTime > 0 ? `${arrivalTime} minutes` : 'Calculating...',
                  distance: bookingData.distance ? `${bookingData.distance} km` : 'Unknown'
                })}
              />

              {/* Route */}
              {directions && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    suppressMarkers: true,
                    polylineOptions: {
                      strokeColor: 'blue',
                      strokeWeight: 4,
                      strokeOpacity: 0.8,
                    }
                  }}
                />
              )}
            </GoogleMap>
          </Box>
        ) : (
          <Box sx={{
            width: '100%',
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '2px dashed #ccc'
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                {t("loading_map")}
              </Typography>
              <CircularProgress sx={{ mt: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t("google_maps_loading")}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Trip Details Floating Card */}
        <Paper
          sx={{
            position: 'absolute',
            top: 60,
            left: 10,
            p: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
            maxWidth: 350,
            zIndex: 1000,
            display: { xs: 'none', md: 'block' }
          }}
        >
          {/* Pickup Section */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: '#4caf50',
                mr: 2,
                mt: 0.5,
                flexShrink: 0
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                {t("pickup")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                {bookingData.pickupAddress}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {bookingData.created_at ?
                  new Date(parseInt(bookingData.created_at)).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }) :
                  '14:30'
                }
              </Typography>
            </Box>
          </Box>

          {/* Destination Section */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: '#f44336',
                mr: 2,
                mt: 0.5,
                flexShrink: 0
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                {t("destination")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                {bookingData.dropAddress}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {t("eta")}: {arrivalTime > 0 ? `${arrivalTime} min` : 'Calculating...'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Marker Info Popup */}
        {selectedMarker && popupPosition && (
          <Paper
            sx={{
              position: 'absolute',
              top: popupPosition.lat > (driverLocation?.lat || bookingData.pickup.lat) ? 100 : 'auto',
              bottom: popupPosition.lat <= (driverLocation?.lat || bookingData.pickup.lat) ? 100 : 'auto',
              left: 20,
              right: 20,
              p: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 2,
              maxWidth: 400,
              zIndex: 1000
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" color="primary">
                {selectedMarker.data.type}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedMarker(null);
                  setPopupPosition(null);
                }}
              >
                ✕
              </IconButton>
            </Box>

            {selectedMarker.type === 'driver' && (
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  {selectedMarker.data.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  ⭐ {selectedMarker.data.rating} • {selectedMarker.data.status}
                </Typography>
                {selectedMarker.data.phone && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon="📋"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedMarker.data.phone);
                      alert( t('number_copied') || 'Phone number copied to clipboard');
                    }}
                    sx={{ mr: 1 }}
                  >
                    Copy
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  Last update: {selectedMarker.data.lastUpdate}
                </Typography>
              </Box>
            )}

            {selectedMarker.type === 'pickup' && (
              <Box>
                <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                  {t("pickup_location")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedMarker.data.address}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("status")}: {t(selectedMarker.data.status)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Created: {selectedMarker.data.time}
                </Typography>
              </Box>
            )}

            {selectedMarker.type === 'destination' && (
              <Box>
                <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                  {t("destination")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedMarker.data.address}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("distance")}: {selectedMarker.data.distance}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("eta")}: {selectedMarker.data.estimatedTime}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t("status")}: {t(selectedMarker.data.status)}
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* Driver Info Panel - Right Side (30%) */}
      <Box sx={{
        flex: '0 0 30%',
        borderRadius: 2,
        height: { md: 'calc(100vh - 100px)', xs: '1000px' },
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        backgroundColor: 'white',
        paddingBottom: '50px'
      }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#333', mb: 3 }}>
            {t('driver_information')}
          </Typography>

          {driverInfo ? (
            <Box>
              {/* Driver Profile */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  src={driverInfo.profile_pic}
                  sx={{
                    width: 60,
                    height: 60,
                    mr: 2,
                    backgroundColor: '#e0e0e0',
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                >
                  {bookingData.driver_name?.charAt(0) || 'D'}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 0.5 }}>
                    {bookingData.driver_name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      ⭐ {bookingData.driverRating}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Trip Details */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('duration')}:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {bookingData.estimate} min
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('distance')}:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {bookingData.distance} km
                  </Typography>
                </Box>
              </Box>

              {/* Copy Button */}
              <Button
                variant="outlined"
                fullWidth
                startIcon="📋"
                disabled={!driverInfo?.driver_contact && !bookingData?.driver_contact}
                sx={{
                  mb: 2,
                  borderColor: '#e0e0e0',
                  color: '#333',
                  textTransform: 'none',
                  py: 1.5,
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: '#f5f5f5'
                  },
                  '&:disabled': {
                    borderColor: '#ccc',
                    color: '#999'
                  }
                }}
                onClick={() => {
                  const phoneNumber = driverInfo?.driver_contact || bookingData?.driver_contact;
                  if (phoneNumber) {
                    navigator.clipboard.writeText(phoneNumber);
                    alert( t('number_copied') || 'Phone number copied to clipboard');
                  } else {
                    console.warn('No driver contact number available');
                    alert('Driver contact number not available');
                  }
                }}
              >
                {t('copy_number')} {driverInfo?.driver_contact || bookingData?.driver_contact || 'N/A'}
              </Button>

              {/* Trip Status */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('trip_status')}:
                </Typography>
                <Chip
                  label={t(bookingData.status).toUpperCase()}
                  size="small"
                  sx={{
                    backgroundColor: bookingData.status === 'STARTED' || bookingData.status === 'ARRIVED' ? '#4caf50' :
                      bookingData.status === 'ACCEPTED' ? '#2196f3' :
                        bookingData.status === 'ARRIVED' ? '#ff9800' : '#9e9e9e',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                {t('driver_information_not_available')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ShareTrip;
